import { onValue, push, ref, set, remove, update, get } from 'firebase/database';
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { SearchOutlined } from '@mui/icons-material';
import ActivityLog from '../activity/ActivityLog';

function DropdownComponent() {
    const [options, setOptions] = useState([]);
    const [newOption, setNewOption] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [editingOptionId, setEditingOptionId] = useState(null);
    const [editText, setEditText] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const dropdownRef = ref(db, 'dropdownOptions/');
        const unsubscribe = onValue(dropdownRef, (snapshot) => {
            const data = snapshot.val();
            const loadedOptions = [];
            for (let key in data) {
                loadedOptions.push({ id: key, text: data[key].text });
            }
            setOptions(loadedOptions);
            setFilteredOptions(loadedOptions.slice(1)); // Skip the first option initially
            setIsLoading(false);
        });
        return () => unsubscribe(); // Clean up the listener

    }, []);

    useEffect(() => {
        setFilteredOptions(
            searchQuery
                ? options.slice(1).filter(option =>
                    option.text.toLowerCase().includes(searchQuery.toLowerCase())
                )
                : options.slice(1)
        );
    }, [searchQuery, options]);


    const addOptionHandler = async () => {
        if (!newOption) {
            alert("Please enter a product type!");
            return;
        }

        const newOptionLowerCase = newOption.toLowerCase();
        const dropdownRef = ref(db, 'dropdownOptions/');
        try {
            const snapshot = await get(dropdownRef);
            const data = snapshot.val();
            const isDuplicate = Object.values(data).some(option =>
                option.text && option.text.toLowerCase() === newOptionLowerCase);
            if (isDuplicate) {
                alert("This product type already exists. Please enter a unique product type.");
                return;
            }
            const newDropdownRef = push(dropdownRef);
            await set(newDropdownRef, {
                text: newOption
            });
            ActivityLog("Add Product Type", `Added product type: ${newOption}`);
            setNewOption('');
        } catch (error) {
            console.error("Error adding product type: ", error);
            alert("Error adding new product type.")
        }
    };

    const deleteOptionHandler = (id, opt) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this product type?");
        if (confirmDelete) {

            const optionRef = ref(db, `dropdownOptions/${id}`);
            remove(optionRef)
                .then(() => {
                    ActivityLog("Delete Product Type", `Deleted product type: ${opt}`);
                    // alert("Product type deleted successfully.");
                })
                .catch((error) => {
                    console.error("Error deleting product type: ", error);
                });
        }
    };

    const startEditHandler = (id, currentText) => {
        setEditingOptionId(id);
        setEditText(currentText);
    };

    const saveEditHandler = async (id, currentText) => {
        if (!editText.trim()) {
            alert("Product type cannot be empty.");
            return;
        }
        const editTextLowerCase = editText.toLowerCase();
        const currentTextLowerCase = currentText.toLowerCase();
        const dropdownRef = ref(db, 'dropdownOptions/');

        try {
            const snapshot = await get(dropdownRef);
            const data = snapshot.val();
            const isDuplicate = Object.values(data).some(option =>
                option.text && option.text.toLowerCase() === editTextLowerCase && option.text.toLowerCase() !== currentTextLowerCase
            );

            if (isDuplicate) {
                alert("This product type already exists. Please enter a unique product type.");
                return;
            }

            // Proceed with updating the dropdown option
            const optionRef = ref(db, `dropdownOptions/${id}`);
            await update(optionRef, { text: editText });
            ActivityLog("Update Product Type", `Updated product type: ${currentText} to ${editText}`);
            alert("Product type updated successfully.");

            // Check and update product types in the products database
            const productsRef = ref(db, 'Products/');
            const productsSnapshot = await get(productsRef);
            const productsData = productsSnapshot.val();

            if (productsData) {
                const updatePromises = Object.keys(productsData).map(productId => {
                    const product = productsData[productId];

                    //if the product's type matches the old type (currentText), update it
                    if (product.type && product.type.toLowerCase() === currentTextLowerCase) {
                        const productRef = ref(db, `Products/${productId}`);
                        return update(productRef, { type: editText })
                            .then(() => {
                                console.log(`Product ID ${productId} updated from type ${currentText} to ${editText}`);
                            })
                            .catch((error) => {
                                console.error("Error updating product type in products: ", error);
                            });
                    }
                    return Promise.resolve();

                });

                await Promise.all(updatePromises);
                console.log("Products updated successfully.");
            }
            setEditText(''); // Reset edit text
            setEditingOptionId(null);
        }
        catch (error) {
            console.error("Error updating product type: ", error);
        }
    };

    return (
        <div className='dropdown'>
            <h3>Add Type of Products</h3>
            <div className="add-product-type">
                <input
                    type="text"
                    value={newOption}
                    onChange={e => setNewOption(e.target.value)}
                    placeholder="Enter new product type"
                />
                <button
                    className='add-product-btn'
                    onClick={addOptionHandler}
                >
                    Add Type
                </button>
            </div>
            <div
                className='search-product-type'
            >
                <input type="text"
                    placeholder="Search product types..."
                    value={searchQuery}
                    onChange={(e) =>
                        setSearchQuery(e.target.value)} />
                <SearchOutlined />
            </div>
            <table className="styled-table">
                <thead>
                    <tr>
                        <th>Product Type</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredOptions.map(opt => (
                        <tr key={opt.id}>
                            <td>
                                {editingOptionId === opt.id ? (
                                    <input
                                        type="text"
                                        value={editText}
                                        onChange={e => setEditText(e.target.value)}
                                    />
                                ) : (
                                    opt.text
                                )}
                            </td>
                            <td>
                                {editingOptionId === opt.id ? (
                                    <>
                                        <button
                                            className="save"
                                            onClick={() => saveEditHandler(opt.id, opt.text)}
                                        >
                                            Save
                                        </button>
                                        <button
                                            className="cancel"
                                            onClick={() => setEditingOptionId(null)}
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            className="edit"
                                            onClick={() => startEditHandler(opt.id, opt.text)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className="delete"
                                            onClick={() => deleteOptionHandler(opt.id, opt.text)}
                                        >
                                            Delete
                                        </button>
                                    </>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>


        </div>
    );
}

export default DropdownComponent;
